package com.tovtalk.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onPlaced
import androidx.compose.ui.layout.positionInRoot
import kotlinx.browser.document
import org.w3c.dom.HTMLIFrameElement

private const val BANNER_AD_CONTAINER_ID = "tovtalk-banner-ad-container"
private var bannerAdCounter = 0

/** Compose Canvas renders into a <canvas>; return its viewport offset so DOM overlays align. */
private fun canvasViewportOffset(): Pair<Double, Double> {
    val canvasEl = document.querySelector("canvas") ?: document.getElementById("root")?.firstElementChild
    return if (canvasEl != null && canvasEl is org.w3c.dom.HTMLElement) {
        val rect = canvasEl.getBoundingClientRect()
        rect.left to rect.top
    } else {
        0.0 to 0.0
    }
}

@Composable
actual fun BannerAdView(
    url: String,
    modifier: Modifier,
) {
    val containerId = remember { "${BANNER_AD_CONTAINER_ID}-${bannerAdCounter++}" }
    val container = remember { createBannerContainer(containerId) }
    val iframe = remember { createBannerIframe(container) }

    DisposableEffect(url) {
        if (url.isNotBlank()) {
            iframe.src = url
        }
        onDispose {
            container.style.display = "none"
            iframe.removeAttribute("src")
            container.parentElement?.removeChild(container)
        }
    }

    Box(
        modifier = modifier
            .onPlaced { coordinates ->
                val size = coordinates.size
                val pos = coordinates.positionInRoot()
                // Compose renders into a <canvas>; offset DOM overlay by the canvas viewport position.
                val (canvasLeft, canvasTop) = canvasViewportOffset()
                val s = container.asDynamic().style
                s.left = "${(canvasLeft + pos.x).toInt()}px"
                s.top = "${(canvasTop + pos.y).toInt()}px"
                s.width = "${size.width}px"
                s.height = "${size.height}px"
                if (url.isNotBlank()) {
                    container.style.display = "block"
                }
            }
    )
}

private fun createBannerContainer(id: String): org.w3c.dom.HTMLElement {
    val container = document.createElement("div") as org.w3c.dom.HTMLElement
    container.id = id
    val s = container.asDynamic().style
    s.position = "fixed"
    s.left = "0px"
    s.top = "0px"
    s.width = "0px"
    s.height = "0px"
    s.zIndex = "10"
    s.overflow = "hidden"
    s.display = "none"
    document.body!!.appendChild(container)
    return container
}

private fun createBannerIframe(container: org.w3c.dom.HTMLElement): HTMLIFrameElement {
    val iframe = document.createElement("iframe") as HTMLIFrameElement
    iframe.style.setProperty("width", "100%")
    iframe.style.setProperty("height", "100%")
    iframe.style.setProperty("border", "none")
    iframe.setAttribute("sandbox", "allow-scripts allow-popups allow-same-origin")
    container.appendChild(iframe)
    return iframe
}
